<template>
  <div class="org-tree-panel" v-loading="loading">
    <el-input
      v-model="filterText"
      placeholder="Поиск"
      prefix-icon="el-icon-search"
      clearable
      size="small"
    >
    </el-input>
    <vue-easy-tree
      ref="tree"
      class="org-tree"
      height="100%"
      :item-size="32"
      :indent="10"
      :data="tree"
      node-key="key"
      :props="defaultProps"
      :filter-node-method="filterNode"
      empty-text="Ничего не найдено"
      expand-on-click-node
      highlight-current
      @current-change="onSelect"
      @node-expand="saveExpanded"
      @node-collapse="saveExpanded"
    >
      <template #default="{ data }">
        <span
          class="node-label"
          :class="{ 'is-unit': !data.isWaste }"
          :title="data.processName"
        >
          {{ data.name }}
        </span>
      </template>
    </vue-easy-tree>
  </div>
</template>

<script>
import VueEasyTree from "@wchbrad/vue-easy-tree/index.js";

// Раскрытые узлы дерева запоминаются между заходами на страницу
const EXPANDED_KEY = "dataEntryNew.treeExpanded";

export default {
  name: "orgTreePanel",
  components: { VueEasyTree },
  data() {
    return {
      loading: false,
      filterText: "",
      // Выделенный отход: клик по подразделению только раскрывает его, выделение остаётся на отходе
      currentKey: null,
      expandedBeforeFilter: null,
      hasScroll: false,
      resizeObserver: null,
      tree: [],
      defaultProps: {
        children: "children",
        label: "name",
      },
    };
  },

  watch: {
    filterText(val, oldVal) {
      if (val && !oldVal) this.expandedBeforeFilter = this.expandedKeys();
      this.$refs.tree.filter(val);
      if (!val && this.expandedBeforeFilter) {
        this.restoreExpanded(this.expandedBeforeFilter);
        this.expandedBeforeFilter = null;
      }
    },
  },

  methods: {
    expandedKeys() {
      const nodesMap = this.$refs.tree.store.nodesMap;
      return new Set(
        Object.keys(nodesMap).filter((key) => nodesMap[key].expanded)
      );
    },

    restoreExpanded(keys) {
      const nodesMap = this.$refs.tree.store.nodesMap;
      Object.keys(nodesMap).forEach((key) => {
        if (keys.has(key)) nodesMap[key].expand();
        else nodesMap[key].collapse();
      });
    },

    // Раскрытие при поиске не запоминаем: после очистки поиска дерево и так возвращается как было.
    // Событие сворачивания приходит до сворачивания узла - сохраняем на следующем тике
    saveExpanded() {
      this.$nextTick(() => {
        if (this.filterText) return;
        try {
          localStorage.setItem(EXPANDED_KEY, JSON.stringify([...this.expandedKeys()]));
        } catch (e) {
          // раскрытие просто не запомнится
        }
      });
    },

    restoreSaved() {
      let keys = null;
      try {
        keys = JSON.parse(localStorage.getItem(EXPANDED_KEY));
      } catch (e) {
        // хранилище недоступно или испорчено - дерево остаётся свёрнутым
      }
      if (Array.isArray(keys)) this.restoreExpanded(new Set(keys));
    },

    // Узел виден, если совпал он сам или любое подразделение над ним:
    // найденное подразделение показывается со всеми своими отходами
    filterNode(value, data, node) {
      if (!value) return true;
      const query = value.toLowerCase();
      for (let current = node; current && current.level > 0; current = current.parent)
        if (current.data.name.toLowerCase().includes(query)) return true;
      return false;
    },

    onSelect(data) {
      if (!data.isWaste) {
        this.setCurrent(this.currentKey);
        return;
      }
      this.currentKey = data.key;
      this.$emit("select", data);
    },

    // Программно выделить узел (без события select), null - снять выделение
    setCurrent(key) {
      this.currentKey = key;
      this.$refs.tree.setCurrentKey(key);
    },

    updateHasScroll() {
      const tree = this.$refs.tree;
      const hasScroll =
        tree.dataList.length * tree.itemSize > tree.$el.clientHeight;
      if (hasScroll === this.hasScroll) return;
      this.hasScroll = hasScroll;
      this.$emit("scroll-change", hasScroll);
    },
  },

  async mounted() {
    this.$watch(() => this.$refs.tree.dataList.length, this.updateHasScroll);
    this.resizeObserver = new ResizeObserver(this.updateHasScroll);
    this.resizeObserver.observe(this.$refs.tree.$el);

    this.loading = true;
    try {
      this.tree = await this.$store.dispatch("getDataEntryTree");
    } finally {
      this.loading = false;
    }
    await this.$nextTick();
    this.restoreSaved();
  },

  beforeDestroy() {
    this.resizeObserver.disconnect();
  },
};
</script>

<style scoped>
.org-tree-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.org-tree {
  flex: 1;
  min-height: 0;
  margin-top: 10px;
  /* Клики раскрывают узлы - текст при этом не выделяется */
  user-select: none;
}
.node-label {
  padding-right: 10px;
}
/* Подразделения жирным, отходы под ними обычным шрифтом */
.node-label.is-unit {
  font-weight: bold;
}
.org-tree ::v-deep .vue-recycle-scroller {
  overflow-x: auto;
}
.org-tree ::v-deep .vue-recycle-scroller__item-wrapper {
  overflow: visible;
}
</style>
